import type { PageTypeV4 } from '@sap/ux-specification-types';
import { v2, PageTypeV2 } from '@sap/ux-specification-types';

/**
 * Method searches in manifest object by passed path and returns found value.
 * If value was not found in manifest object,
 * then method created empty object in manifest object within passed path.
 *
 * @param manifest - target manifest object
 * @param path - path to lookup in manifest
 * @returns Found or created value
 */
export const ensureManifestSectionByPathV2 = (manifest: object, path: string): object => {
    let manifestSection = manifest;
    path.split('/').forEach(function (element) {
        if (!manifestSection[element]) {
            manifestSection[element] = {};
        }
        manifestSection = manifestSection && manifestSection[element];
    });
    return manifestSection ? manifestSection : {};
};

/**
 * Method searches in manifest object by passed path and returns found value.
 * If value was not found in manifest object, then 'undefined' is returned.
 *
 * @param manifest - target manifest file
 * @param path - path to lookup in manifest
 * @returns Found value
 */
export const getManifestSectionByPathV2 = (manifest: object, path: string): object | undefined => {
    let manifestSection = manifest;
    const elements = path.split('/');
    for (const element of elements) {
        if (!manifestSection[element]) {
            return undefined;
        }
        manifestSection = manifestSection && manifestSection[element];
    }
    return manifestSection ? manifestSection : {};
};

export const deleteManifestSectionByPathV2 = (manifest: object, path: string): void => {
    let manifestSection = manifest;
    for (let index = 0; index < path.split('/').length; index++) {
        const element = path.split('/')[index];
        if (index === path.split('/').length - 1) {
            delete manifestSection[element];
        } else {
            manifestSection = manifestSection && manifestSection[element];
        }
    }
};

/**
 * Creates a new page template object based on the provided page type.
 *
 * @param pageType - The type of the page (PageTypeV2 or PageTypeV4).
 * @returns An object representing the new page template.
 */
function getNewPageTemplate(pageType: PageTypeV2 | PageTypeV4): object {
    return {
        component: {
            name: 'sap.suite.ui.generic.template.' + pageType
        }
    };
}

/**
 * Method finds matching pages by 'entitySet' and 'navigationProperty' in passed pages object.
 *
 * @param pages Object with pages.
 * @param [entitySet] Entity set.
 * @param [navigationProperty] Navigation property.
 * @returns Matching page object.
 */
function findPageInManifest(
    pages: v2.SapUiAppPagesV2,
    entitySet?: string,
    navigationProperty?: string
): v2.SapUiAppPageV2 | undefined {
    if (!pages) {
        return;
    }
    for (const pageId in pages) {
        const page = pages[pageId];
        if (page.entitySet === entitySet && page.navigationProperty === navigationProperty) {
            return page;
        }
    }
}

/**
 * Transforms a page from the configuration object to the manifest object.
 *
 * @param configPages - The configuration object containing page definitions.
 * @param association - The association key for the page.
 * @param manifestPage - The manifest object where the page is being transformed.
 */
function transformPageFromAssociation(configPages: v2.Pages, association: string, manifestPage: object) {
    //Handling partial deletion of a page
    if (!configPages[association]) {
        return;
    }
    if (manifestPage['pages'] && !Object.values(manifestPage['pages'])[0]) {
        manifestPage['pages'][`${configPages[association].pageType}|${configPages[association].entitySet}`] =
            getNewPageTemplate(configPages[association].pageType);
    }
    if (!manifestPage['pages']) {
        manifestPage['pages'] = {
            [`${configPages[association].pageType}|${configPages[association].entitySet}`]: getNewPageTemplate(
                configPages[association].pageType
            )
        };
    }
    if (!Array.isArray(manifestPage['pages'])) {
        let associatedManifestPage = findPageInManifest(
            manifestPage['pages'],
            configPages[association].entitySet,
            configPages[association].navigationProperty
        );

        if (!associatedManifestPage) {
            manifestPage['pages'][`${configPages[association].pageType}|${configPages[association].entitySet}`] =
                associatedManifestPage = getNewPageTemplate(configPages[association].pageType);
        }

        transformPageToNewManifestV2(association, configPages, associatedManifestPage);
    }
}

/**
 * Deletes a page from the manifest object if it is not present in the configuration object.
 *
 * @param manifestPage - The manifest object where the page is being deleted or updated.
 * @param configPages - The configuration object containing all page definitions.
 * @param configPage - The specific page configuration being processed.
 */
function deletePage(manifestPage: object, configPages: v2.Pages, configPage: v2.Page) {
    if (manifestPage['pages']) {
        Object.keys(manifestPage['pages']).forEach((key) => {
            const currentPage = manifestPage['pages'][key];
            if (currentPage.component?.name === v2.FE_TEMPLATE_V2_OBJECT_PAGE) {
                const configKey = `ObjectPage_${currentPage.entitySet}`;
                if (!configPages[configKey]) {
                    delete manifestPage['pages'][key];
                }
            }
        });
    }
    if (
        (configPage.pageType === PageTypeV2.ListReport || configPage.pageType === PageTypeV2.AnalyticalListPage) &&
        Object.keys(configPage.navigation).length === 0
    ) {
        delete manifestPage['pages'];
    }
}

/**
 * Transfers pages and routings from manifest to the config pages.
 *
 * @param pageId - page ID
 * @param configPages - config definitions of each page
 * @param manifestPage - top page definition in manifest
 */
export function transformPageToNewManifestV2(pageId: string, configPages: v2.Pages, manifestPage: object): void {
    const configPage = configPages[pageId];
    if (!configPage) {
        return;
    }
    if (configPage.entitySet) {
        manifestPage['entitySet'] = configPage.entitySet;
    }
    if (configPage.pageType === PageTypeV2.ListReport) {
        manifestPage['component']['list'] = true;
    }
    if (configPage.navigationProperty) {
        manifestPage['navigationProperty'] = configPage.navigationProperty;
    }
    if (configPage.defaultLayoutType) {
        manifestPage['defaultLayoutType'] = configPage.defaultLayoutType;
    } else if ('defaultLayoutType' in manifestPage) {
        delete manifestPage['defaultLayoutType'];
    }
    if (configPage.navigation) {
        for (const association in configPage.navigation) {
            transformPageFromAssociation(configPages, association, manifestPage);
        }
        // Delete page
        if (!Array.isArray(manifestPage['pages'])) {
            deletePage(manifestPage, configPages, configPage);
        }
    }
}
